// Superadmin → Broadcasts: send announcements (version updates, maintenance,
// downtime, policy, emergencies…) to everyone or a targeted audience, in real
// time over Socket.IO. Backend: user-service/src/modules/broadcast.
import React, { useMemo, useState } from "react";
import { Megaphone, Send, LayoutTemplate, Users, History, CalendarClock, CheckCircle2 } from "lucide-react";
import StatsGrid from "../../components/StatsGrid";
import AlertModal from "../../components/AlertModal";
import { SkeletonStyles, SkeletonBox, LoadingPill, SkeletonStatCard } from "../../components/SkeletonLoader";
import { getResponsiveStyles } from "../../utils/styleUtils";
import { BroadcastPageProvider, useBroadcastPage } from "./context";
import { getBroadcastGlobalCss } from "./styles";
import ComposeTab from "./components/ComposeTab";
import TemplatesTab from "./components/TemplatesTab";
import AudiencesTab from "./components/AudiencesTab";
import HistoryTab from "./components/HistoryTab";

const BroadcastsPage = () => {
  const { s, t, theme, language, isRTL, screenSize, loading, templates, audiences, counts, alert, closeAlert, refreshCounts } =
    useBroadcastPage();
  const [tab, setTab] = useState("compose");
  const [seed, setSeed] = useState(null);
  const [historyKey, setHistoryKey] = useState(0);

  const globalCss = useMemo(() => getBroadcastGlobalCss(theme), [theme]);
  // Shared stat-card look (same source as Departments / Employee Management).
  const sharedStyles = useMemo(
    () => getResponsiveStyles({ ...screenSize, isDesktop: !screenSize.isMobile && !screenSize.isTablet }, theme, language),
    [screenSize, theme, language]
  );

  const tabs = [
    { id: "compose", label: t.tabs.compose, icon: Send },
    { id: "templates", label: t.tabs.templates, icon: LayoutTemplate, count: templates.length },
    { id: "audiences", label: t.tabs.audiences, icon: Users, count: audiences.length },
    { id: "history", label: t.tabs.history, icon: History },
  ];

  const applyTemplate = (template) => {
    setSeed({ template, at: Date.now() });
    setTab("compose");
  };
  const editBroadcast = (broadcast) => {
    setSeed({ broadcast, at: Date.now() });
    setTab("compose");
  };

  const header = (
    <div className="nx-page-header" style={s.header}>
      <div style={s.headerText}>
        <h1 className="nx-page-title" style={s.title}>
          <Megaphone size={screenSize.isMobile ? 24 : 28} />
          {t.title}
        </h1>
        <p style={s.subtitle}>{t.subtitle}</p>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="bc-scope nx-flush-mobile" style={s.container}>
        <SkeletonStyles theme={theme} />
        <LoadingPill text={t.loading} theme={theme} isRTL={isRTL} />
        <div style={{ ...s.header, animation: "none" }}>
          <div style={s.headerText}>
            <SkeletonBox width="220px" height="30px" style={{ marginBottom: "12px" }} />
            <SkeletonBox width="360px" height="16px" style={{ maxWidth: "100%" }} />
          </div>
        </div>
        <div style={s.statsGrid}>
          {[1, 2, 3, 4].map((i) => (
            <SkeletonStatCard key={i} cardStyle={{ ...sharedStyles.statCard, marginBottom: 0 }} />
          ))}
        </div>
        <div style={s.sectionCard}>
          <SkeletonBox width="200px" height="22px" style={{ marginBottom: "20px" }} />
          <SkeletonBox width="100%" height="120px" />
        </div>
      </div>
    );
  }

  return (
    <div className="bc-scope nx-flush-mobile" style={s.container}>
      <style>{globalCss}</style>
      <SkeletonStyles theme={theme} />

      {header}

      <div style={s.statsGrid}>
        <StatsGrid
          styles={{ ...sharedStyles, container: { direction: isRTL ? "rtl" : "ltr" } }}
          screenSize={screenSize}
          layout="inline"
          gridStyle={{ gridTemplateColumns: "repeat(auto-fit, minmax(min(200px, 100%), 1fr))", marginBottom: 0 }}
          stats={[
            { title: t.stats.templates, value: templates.filter((x) => x.isActive).length, icon: LayoutTemplate, iconBackground: "rgb(46, 124, 186)" },
            { title: t.stats.audiences, value: audiences.length, icon: Users, iconBackground: "rgb(6, 0, 180)" },
            { title: t.stats.scheduled, value: counts.scheduled ?? "—", icon: CalendarClock, iconBackground: "rgb(124, 58, 237)" },
            { title: t.stats.sent, value: counts.sent ?? "—", icon: CheckCircle2, iconBackground: "rgb(0, 180, 72)" },
          ]}
        />
      </div>

      <div style={s.tabsCard}>
        <div className="nx-tabs-scroll" style={s.tabContainer} role="tablist">
          {tabs.map(({ id, label, icon: Icon, count }) => (
            <button key={id} type="button" role="tab" aria-selected={tab === id} className="bc-tab" style={s.tab(tab === id)} onClick={() => setTab(id)}>
              <Icon size={16} />
              {label}
              {count > 0 && <span style={s.countPill}>{count}</span>}
            </button>
          ))}
        </div>
      </div>

      {/* Compose stays mounted so a half-written draft survives tab switches. */}
      <div style={{ display: tab === "compose" ? "block" : "none" }}>
        <ComposeTab
          seed={seed}
          onDone={() => {
            setHistoryKey((k) => k + 1);
            refreshCounts();
            setTab("history");
          }}
        />
      </div>
      {tab === "templates" && <TemplatesTab onUse={applyTemplate} />}
      {tab === "audiences" && <AudiencesTab />}
      {tab === "history" && <HistoryTab onEdit={editBroadcast} refreshKey={historyKey} />}

      <AlertModal
        open={alert.open}
        title={alert.title}
        message={alert.message}
        variant={alert.variant}
        onConfirm={closeAlert}
        onClose={closeAlert}
        theme={theme}
        language={language}
      />
    </div>
  );
};

const Broadcasts = () => (
  <BroadcastPageProvider>
    <BroadcastsPage />
  </BroadcastPageProvider>
);

export default Broadcasts;
